import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AuthProviderIcon } from '@/components/auth/AuthProviderIcon';
import { formatCompact } from '@/lib/money';
import type { DashboardBreakdownItem } from '../api';

/** Short Figma-style labels for known methods. */
function methodDisplayLabel(item: DashboardBreakdownItem): string {
  const key = (item.key ?? '').toLowerCase();
  if (key === 'phone') return 'Mobile';
  if (key === 'apple') return 'Apple';
  if (key === 'email') return 'Email';
  if (key === 'google') return 'Google';
  return item.label ?? item.key ?? 'Unknown';
}

/**
 * Confluence §7 / Figma Sign-up Methods — ranked rows with provider glyphs.
 */
export function DashboardSignupMethodsCard({
  items,
  total,
  isLoading,
}: {
  items: DashboardBreakdownItem[];
  total?: number;
  isLoading?: boolean;
}) {
  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardHeader className="space-y-1 p-4 pb-2">
        <CardTitle className="text-sm font-medium text-sky-400">Sign-up Methods</CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Initial registration method
          {total != null ? ` · ${formatCompact(total)} total` : ''}
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <p className="px-4 py-6 text-center text-xs text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-muted-foreground">No sign-ups in this period.</p>
        ) : (
          <ul className="divide-y divide-border/30">
            {items.map((item, index) => {
              const pct = Math.max(0, Math.min(100, Number(item.percent ?? 0)));
              const label = methodDisplayLabel(item);
              return (
                <li
                  key={item.key ?? label}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm"
                >
                  <span className="w-4 shrink-0 text-xs tabular-nums text-muted-foreground">
                    {index + 1}
                  </span>
                  <AuthProviderIcon loginMethod={item.key ?? label} size={18} />
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                    {label}
                  </span>
                  <span className="shrink-0 tabular-nums font-medium text-foreground">
                    {formatCompact(item.count ?? 0)}
                  </span>
                  <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {pct.toFixed(0)}%
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
